<template>
  <div class="ai-chat-container">
    <!-- 左侧会话列表 -->
    <div class="chat-sidebar">
      <div class="sidebar-header">
        <el-button
          type="primary"
          icon="el-icon-plus"
          size="small"
          class="new-chat-btn"
          @click="handleNewChat"
        >
          新建对话
        </el-button>
      </div>

      <div class="session-list">
        <div
          v-for="item in sessionList"
          :key="item.id"
          class="session-item"
          :class="{ active: item.id === currentSessionId }"
          @click="handleSelectSession(item)"
        >
          <i class="el-icon-chat-dot-round session-icon" />
          <span class="session-title" :title="item.title">{{ item.title }}</span>
          <i
            class="el-icon-delete delete-icon"
            @click.stop="handleDeleteSession(item)"
          />
        </div>
      </div>
    </div>

    <!-- 右侧聊天主区域 -->
    <div class="chat-main">
      <!-- 右侧上部：聊天窗口 -->
      <div class="chat-window" ref="chatWindowRef">
        <div v-if="currentMessages.length === 0" class="empty-chat">
          <i class="el-icon-cpu empty-icon" />
          <p>你好！我是你的 AI 助手，请问有什么我可以帮你的吗？</p>
        </div>

        <div
          v-for="(msg, index) in currentMessages"
          :key="index"
          class="message-row"
          :class="{ 'user-row': msg.role === 'user', 'ai-row': msg.role === 'assistant' }"
        >
          <!-- 头像 -->
          <div class="avatar">
            <i :class="msg.role === 'user' ? 'el-icon-user-solid' : 'el-icon-cpu'" />
          </div>

          <!-- 消息气泡 -->
          <div class="message-content-wrap">
            <div class="sender-name">{{ msg.role === 'user' ? '我' : 'AI 助手' }}</div>
            <div class="message-bubble">
              <div class="message-text" v-html="formatContent(msg.content)" />
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧下部：输入窗口 -->
      <div class="input-area">
        <!-- 模型选择与辅助工具栏 -->
        <div class="input-toolbar">
          <div class="model-selector">
            <span class="label">当前模型：</span>
            <el-select v-model="selectedModel" size="mini" placeholder="选择模型" style="width: 160px;">
              <el-option
                v-for="model in modelOptions"
                :key="model.value"
                :label="model.label"
                :value="model.value"
              >
                <span style="float: left">{{ model.label }}</span>
                <span style="float: right; color: #8492a6; font-size: 12px;">{{ model.tag }}</span>
              </el-option>
            </el-select>
          </div>
          <div class="toolbar-actions">
            <el-button type="text" size="mini" icon="el-icon-delete" @click="handleClearCurrentChat">清空当前对话</el-button>
          </div>
        </div>

        <!-- 文本输入框 -->
        <div class="input-box-wrapper">
          <el-input
            v-model="inputMessage"
            type="textarea"
            :rows="3"
            placeholder="给 AI 发送消息... (Ctrl + Enter 发送)"
            resize="none"
            @keydown.native="handleKeyDown"
          />
          <div class="send-action-bar">
            <span class="tip">Ctrl + Enter 发送</span>
            <el-button
              type="primary"
              size="small"
              icon="el-icon-s-promotion"
              :loading="sending"
              :disabled="!inputMessage.trim()"
              @click="handleSend"
            >
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiChat',
  data() {
    return {
      // 模型选项
      selectedModel: 'qwen-max',
      modelOptions: [
        { label: 'Qwen 3.7 Max', value: 'qwen-max', tag: '通义千问' },
        { label: 'GPT-4o', value: 'gpt-4o', tag: 'OpenAI' },
        { label: 'Claude 3.5 Sonnet', value: 'claude-3-5', tag: 'Anthropic' }
      ],

      // 会话列表
      currentSessionId: 1,
      sessionList: [
        { id: 1, title: 'Kubernetes 集群部署排错' },
        { id: 2, title: 'C# .NET Core 9 性能优化' },
        { id: 3, title: 'QML 聊天界面样式调整' }
      ],

      // 聊天记录字典，key 为 sessionId，value 为消息数组
      messagesMap: {
        1: [
          { role: 'user', content: '我的 Rocky Linux 9 节点 Kubernetes Calico 插件一直报错，怎么排查？' },
          { role: 'assistant', content: '排查 Calico 插件异常通常可以从以下几个步骤入手：<br>1. 检查节点间 BGP 连通性。<br>2. 查看 <code>calico-node</code> Pod 日志。<br>3. 检查网卡 IPAutoDetection 策略。' }
        ],
        2: [
          { role: 'user', content: 'SqlSugar ORM 如何在 .NET 中配置多租户？' },
          { role: 'assistant', content: 'SqlSugar 支持通过 <code>ConnectionConfig</code> 的 <code>ConfigureExternalServices</code> 来动态切换分库或多租户数据库连接。' }
        ],
        3: [
          { role: 'user', content: '写一个类似 ChatGPT 的左侧会话右侧聊天界面。' },
          { role: 'assistant', content: '没问题，我已经为你编写了 <code>AiChat.vue</code> 组件，请参考左侧列表与右侧上下分栏布局。' }
        ]
      },

      inputMessage: '',
      sending: false
    }
  },
  computed: {
    // 当前会话的消息列表
    currentMessages() {
      return this.messagesMap[this.currentSessionId] || []
    }
  },
  updated() {
    this.scrollToBottom()
  },
  methods: {
    // 切换会话
    handleSelectSession(item) {
      this.currentSessionId = item.id
    },

    // 新建对话
    handleNewChat() {
      const newId = Date.now()
      const newSession = {
        id: newId,
        title: '新对话 ' + (this.sessionList.length + 1)
      }
      this.sessionList.unshift(newSession)
      this.$set(this.messagesMap, newId, [])
      this.currentSessionId = newId
    },

    // 删除会话
    handleDeleteSession(item) {
      this.$confirm(`确定要删除对话 "${item.title}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.sessionList.findIndex(s => s.id === item.id)
        if (index !== -1) {
          this.sessionList.splice(index, 1)
          delete this.messagesMap[item.id]
          if (this.currentSessionId === item.id && this.sessionList.length > 0) {
            this.currentSessionId = this.sessionList[0].id
          }
        }
        this.$message.success('删除成功')
      }).catch(() => {})
    },

    // 清空当前对话
    handleClearCurrentChat() {
      this.$set(this.messagesMap, this.currentSessionId, [])
      this.$message.info('当前对话已清空')
    },

    // 快捷键发送 (Ctrl + Enter)
    handleKeyDown(e) {
      if (e.keyCode === 13 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        this.handleSend()
      }
    },

    // 发送消息
    handleSend() {
      const text = this.inputMessage.trim()
      if (!text || this.sending) return

      // 确保当前会话有消息数组
      if (!this.messagesMap[this.currentSessionId]) {
        this.$set(this.messagesMap, this.currentSessionId, [])
      }

      // 添加用户消息
      this.messagesMap[this.currentSessionId].push({
        role: 'user',
        content: text
      })

      this.inputMessage = ''
      this.sending = true

      // 模拟 AI 异步流式回复
      setTimeout(() => {
        this.messagesMap[this.currentSessionId].push({
          role: 'assistant',
          content: `收到你的问题！当前选择的模型是 <b>${this.selectedModel}</b>。<br>这是一条模拟的 AI 回复响应内容。`
        })
        this.sending = false
      }, 1000)
    },

    // 滚动到底部
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatWindowRef
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    // 格式化文本（支持简单 HTML 标签如 code 等）
    formatContent(content) {
      return content || ''
    }
  }
}
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  height: calc(100vh - 84px);
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  overflow: hidden;
}

/* 左侧会话列表 */
.chat-sidebar {
  width: 260px;
  background-color: #ffffff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #f2f3f5;
}

.new-chat-btn {
  width: 100%;
}

.session-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.session-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 4px;
  color: #606266;
  font-size: 14px;
  transition: background-color 0.2s;
  position: relative;
}

.session-item:hover {
  background-color: #f5f7fa;
}

.session-item.active {
  background-color: #ecf5ff;
  color: #409eff;
  font-weight: 500;
}

.session-icon {
  margin-right: 8px;
  font-size: 16px;
}

.session-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-icon {
  display: none;
  color: #909399;
  font-size: 14px;
}

.session-item:hover .delete-icon {
  display: block;
}

.delete-icon:hover {
  color: #f56c6c;
}

/* 右侧主聊天区域 */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  position: relative;
}

/* 右侧上部：聊天窗口 */
.chat-window {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background-color: #fafbfc;
}

.empty-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 12px;
}

.message-row {
  display: flex;
  margin-bottom: 20px;
}

.message-row.user-row {
  flex-direction: row-reverse;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #606266;
  flex-shrink: 0;
}

.user-row .avatar {
  background-color: #409eff;
  color: #fff;
  margin-left: 12px;
}

.ai-row .avatar {
  background-color: #67c23a;
  color: #fff;
  margin-right: 12px;
}

.message-content-wrap {
  max-width: 70%;
}

.user-row .message-content-wrap {
  text-align: right;
}

.sender-name {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-all;
  display: inline-block;
  text-align: left;
}

.ai-row .message-bubble {
  background-color: #ffffff;
  color: #303133;
  border: 1px solid #ebeef5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.user-row .message-bubble {
  background-color: #409eff;
  color: #ffffff;
}

/* 右侧下部：输入窗口 */
.input-area {
  border-top: 1px solid #e4e7ed;
  background-color: #ffffff;
  padding: 12px 24px 16px 24px;
}

.input-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.model-selector {
  display: flex;
  align-items: center;
}

.model-selector .label {
  font-size: 13px;
  color: #606266;
  margin-right: 8px;
}

.input-box-wrapper ::v-deep .el-textarea__inner {
  border: none;
  padding: 0;
  box-shadow: none;
  font-family: inherit;
}

.send-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.send-action-bar .tip {
  font-size: 12px;
  color: #c0c4cc;
}
</style>
